import { ReactElement, useState } from 'react';
import { saveAs } from 'file-saver';
import { FieldValues, useForm } from 'react-hook-form';
import {
  Checkbox,
  StatusBar,
  Modal,
  PrimaryButton,
  RadioButton,
  SecondaryButton,
  SnackbarStatus,
  Select,
  Typography,
} from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import ModalImg from 'src/assets/images/chef-plating.png';
import { FileIcon } from 'src/assets/images/FileIcon';
import { exportCSV } from 'src/api/exportCSV/exportCSV';
import { exportAllergensPDF } from 'src/api/exportAllergensPDF';
import { triggerPriceSchemeExport } from 'src/api/triggerPriceSchemeExport/triggerPriceSchemeExport';
import { error } from 'src/mocks/data';
import { IAsset } from 'src/helpers/menus/menus.types';
import { formatDate } from 'src/helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { getMenuDataToSave, ISavedMenuData } from './utilities/getMenuDataToSave';
import styles from './ExportModal.module.scss';
import { IAssetV2 } from 'src/api/getMenu/types';

export enum ExportFileType {
  COMTREX_CSV = 'comtrex-csv',
  MENU_PRICES_CSV = 'menu-prices-csv',
  ALLERGEN_PDF = 'allergen-pdf',
}

export interface IExportModal {
  title: string;
  closeModal: () => void;
  open: boolean;
  id: string;
  name: string;
  allergensPDF: IAsset | IAssetV2 | null;
  priceSchemes?: { name: string; value: string }[];
}

const ExportModal = ({
  title,
  closeModal,
  open,
  id,
  name,
  allergensPDF,
  priceSchemes = [],
}: IExportModal): ReactElement => {
  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      file: ExportFileType.COMTREX_CSV,
      showPotentialAllergens: true,
    },
  });
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const fileValue = watch('file');
  const [selectedPriceSchemeId, setSelectedPriceSchemeId] = useState<string | undefined>();

  const date = allergensPDF ? formatDate(new Date(allergensPDF.create_date)) : '';
  const time = allergensPDF ? formatTime(new Date(allergensPDF.create_date)) : '';

  const savedMenusData: ISavedMenuData[] | null = JSON.parse(sessionStorage.getItem('pdfGeneration'));
  const savedMenuData: ISavedMenuData = savedMenusData?.find((data: ISavedMenuData) => data?.menuId === id);
  const assetTime = allergensPDF ? new Date(allergensPDF.create_date) : {};
  const isGeneratingPDF = savedMenuData && (!allergensPDF || assetTime < new Date(savedMenuData.startTime));

  const onSubmit = async (data: FieldValues) => {
    clearError();
    if (data.file === ExportFileType.COMTREX_CSV) {
      try {
        const response = await exportCSV({
          payload: { destinations: ['COMTREX'] },
          menuId: id,
        });

        saveAs(response, `${name}.csv`);
        showNotification({
          message: `You have successfully exported menu ${name}.`,
          status: SnackbarStatus.SUCCESS,
        });
        closeModal();
      } catch {
        showNotification({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' });
      }
    } else if (data.file === ExportFileType.MENU_PRICES_CSV) {
      try {
        await triggerPriceSchemeExport(selectedPriceSchemeId, id);
        closeModal();
      } catch {
        showNotification(error);
      }
    } else if (data.file === ExportFileType.ALLERGEN_PDF) {
      try {
        await exportAllergensPDF(id, data.showPotentialAllergens);

        const dataToSave = getMenuDataToSave(id, savedMenusData);
        sessionStorage.setItem('pdfGeneration', JSON.stringify(dataToSave));

        showNotification({
          message: 'New Allergen PDF is being generated',
          status: SnackbarStatus.INFO,
        });
      } catch {
        showNotification({
          message: "New file can't be generated, please try again later",
          status: SnackbarStatus.ERROR,
        });
      }
    }
  };

  const handlePDFFileDownload = async () => {
    const pdfUrl = 'asset_url' in allergensPDF ? allergensPDF.asset_url : allergensPDF?.asset_variants[0]?.url;

    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      saveAs(blob, `${name}-${date}-${time}-allergen.pdf`);
    } catch {
      showNotification({ status: SnackbarStatus.ERROR, message: 'Internal Server Error' });
    }
  };

  return (
    <Modal closeModal={closeModal} open={open} image={ModalImg} imageAlt="Picture of a chef plating up">
      <form onSubmit={handleSubmit(onSubmit)} className={styles['export-form']}>
        <Typography size="xxlarge" weight="semi-bold">
          {title}
        </Typography>
        <div className={styles['options-container']}>
          <RadioButton name="file" value="comtrex-csv" register={register}>
            Menu Details (CSV)
          </RadioButton>

          <div className={styles['menu-prices-radio']}>
            <RadioButton name="file" value="menu-prices-csv" register={register}>
              <Typography>Menu prices (CSV)*</Typography>
              <Typography size="small" className={styles['radio-label-subtitle']}>
                *This export excludes bundle &amp; modifier override information
              </Typography>
            </RadioButton>
          </div>
          <div className={styles['price-scheme-select']}>
            <Select
              label="Choose a price scheme"
              options={priceSchemes}
              onChange={(selected) => setSelectedPriceSchemeId(selected)}
              value={selectedPriceSchemeId}
              disabled={fileValue !== 'menu-prices-csv'}
            />
          </div>

          <div>
            <RadioButton name="file" value="allergen-pdf" register={register}>
              Allergen information (PDF)
            </RadioButton>
            {fileValue === 'allergen-pdf' && (
              <div className={styles['pdf-message-box']}>
                {!isGeneratingPDF && !allergensPDF && <Typography>Generate file below to download export</Typography>}
                {isGeneratingPDF && !allergensPDF && (
                  <Typography>This might take a few mins, please come back later and refresh the page.</Typography>
                )}
                {allergensPDF && (
                  <div className={styles['file']}>
                    <FileIcon className={styles['file-icon']} />
                    <Typography className={styles['file-text']}>{`File generated on ${date} at ${time}`}</Typography>
                  </div>
                )}

                <div className={styles['show-may-contain-checkbox']}>
                  <Checkbox
                    name="showPotentialAllergens"
                    register={register}
                    label="Include 'May Contain' information"
                    disabled={isSubmitting || isGeneratingPDF}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {fileValue === 'comtrex-csv' || fileValue === 'menu-prices-csv' ? (
          <div className={styles['controls']}>
            <SecondaryButton size="full-width" buttonStyle="outline" type="button" onClick={closeModal}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              size="full-width"
              loading={isSubmitting}
              disabled={fileValue === 'menu-prices-csv' && !selectedPriceSchemeId}
              type="submit"
            >
              {isSubmitting ? 'Downloading...' : 'Download'}
            </PrimaryButton>
          </div>
        ) : (
          <div className={styles['controls']}>
            {allergensPDF ? (
              <SecondaryButton
                buttonStyle="outline"
                loading={isGeneratingPDF || isSubmitting}
                disabled={isGeneratingPDF || isSubmitting}
                size="full-width"
                type="submit"
              >
                {isGeneratingPDF || isSubmitting ? 'New file generating' : 'Regenerate file'}
              </SecondaryButton>
            ) : (
              <PrimaryButton
                loading={isGeneratingPDF || isSubmitting}
                disabled={isGeneratingPDF || isSubmitting}
                size="full-width"
                type="submit"
              >
                {isGeneratingPDF || isSubmitting ? 'New file generating' : 'Generate'}
              </PrimaryButton>
            )}
            {allergensPDF && (
              <PrimaryButton size="full-width" onClick={handlePDFFileDownload} type="button">
                Download
              </PrimaryButton>
            )}
          </div>
        )}
        <div className={styles['error-banner']}>
          {errorMessage && <StatusBar message={errorMessage} variant="error" />}
        </div>
      </form>
    </Modal>
  );
};

export default ExportModal;
